/*
 * Copyright © 2026 Medaica, Inc
 *
 * All rights reserved.
 *
 * This code is confidential and proprietary information belonging to Medaica, Inc.
 * Unauthorized copying, distribution, or use of this code, in whole or in part,
 * is strictly prohibited, and may constitute a violation of intellectual property rights.
 *
 * If you have received this code in error, please notify the owner immediately
 * at support@medaica.com and delete this file from your system.
 */

/* eslint-disable @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-explicit-any --
 * The report document and pcg-report core are loose-typed by design: the
 * core is vendored prototype JS with a hand-written surface .d.ts. */

import React, { ReactElement, useState } from "react"
import {
  STRUCTURAL_SOURCE_HINT,
  isStructuralSoundReviewFeature,
  structuralSourceRecordings,
  structuralSourceStatusText,
  structuralSourceUnavailableReason,
} from "./core"

/**
 * "Accepted Structural Sound recordings" — every recording the review
 * accepted for Structural Sound, one radio row each (the prototype's
 * structuralSourceChecklistHTML, Heart Report v0.5.8): the recording the
 * card's value came from is selected; any other assessable recording can be
 * chosen instead; a recording that cannot support a result stays on the
 * list, disabled, with the reason. `onSelect` applies the choice and
 * answers with the reason it was refused, or null; the owner re-renders
 * after a change.
 */

const slug = (value: string): string => value.replace(/[^a-z0-9]+/gi, "-").toLowerCase()

const HeartReportStructuralSources = ({
  feature,
  editable,
  onSelect,
}: {
  feature: any
  editable: boolean
  onSelect: (recordingId: string) => string | null
}): ReactElement | null => {
  const [refusal, setRefusal] = useState<{ text: string } | null>(null)
  const records = structuralSourceRecordings(feature)
  if (!isStructuralSoundReviewFeature(feature) || records.length === 0) {
    return null
  }
  const name = String(feature.name)
  const groupName = `structural-source-${slug(name)}`
  return (
    <section className="phr-timing-sources phr-structural-sources" data-testid={`structural-sources-${name}`}>
      <div className="phr-timing-sources-head">
        <strong>Accepted Structural Sound recordings</strong>
      </div>
      <div className="phr-timing-sources-hint">{STRUCTURAL_SOURCE_HINT}</div>
      {records.map((source: any) => {
        const recordingId = String(source.recordingId ?? "")
        const assessable = source.assessable === true
        const inputId = `${groupName}-${slug(recordingId)}`
        const reason = structuralSourceUnavailableReason(source)
        return (
          <div
            className={`phr-timing-source-row${assessable ? "" : " phr-structural-source-row--unavailable"}`}
            key={recordingId}
          >
            <input
              type="radio"
              id={inputId}
              name={groupName}
              value={recordingId}
              checked={assessable && source.selected === true}
              disabled={!editable || !assessable}
              onChange={() => {
                const refused = onSelect(recordingId)
                setRefusal(refused ? { text: refused } : null)
              }}
            />
            <label
              htmlFor={inputId}
              className={`phr-timing-source-label${editable && assessable ? "" : " phr-timing-source-label--locked"}`}
            >
              <span className="phr-timing-source-position">
                {String(source.position || recordingId || "Recording")}
              </span>
              <span
                className={`phr-timing-source-rationale${assessable ? "" : " phr-structural-source-reason"}`}
                data-testid={`structural-source-status-${slug(recordingId)}`}
              >
                {structuralSourceStatusText(source)}
                {reason ? (
                  <>
                    <br />
                    {reason}
                  </>
                ) : null}
              </span>
            </label>
          </div>
        )
      })}
      {refusal ? (
        <div className="phr-timing-sources-refusal" role="alert">
          {refusal.text}
        </div>
      ) : null}
    </section>
  )
}

export default HeartReportStructuralSources
